import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "../../Context/useAuth";
import { ApiError } from "../../Models/ApiError";
import { toast } from "react-toastify";
import { dataContext } from "../../Context/contextProvider";
import { observer } from "mobx-react";
import { useOrder } from "../../Context/useOrder";
import { ValidationResult } from "../../Models/ValidationResult";
import ModalRegisterDevice from "./ModalRegisterDevice";
import { LoginToServerResponse } from "../../Models/LoginToServerResponse";
import { LoginToServerRequest } from "../../Models/LoginToServerRequest";

interface ServerLoginValidationErrors {
  serverUrl?: ValidationResult;
  serverPassword?: ValidationResult;
}

interface Step1Props {
  onStepComplete?: (serverUrl: string) => void;
}
// login to server
const Step1 = observer(({ onStepComplete }: Step1Props) => {
  const { getDeviceId, loginToServer } = useAuth();
  const { setCurrentOrder } = useOrder();
  const dataStore = useContext(dataContext);
  const [submitDisabled, setSubmitDisabled] = useState<boolean>(false);
  const [previousSessions, setPreviousSessions] = useState<string[]>([]);
  const [localServerUrl, setLocalServerUrl] = useState<string>("");
  const [localServerPassword, setLocalServerPassword] = useState<string>("");
  const [errors, setErrors] = useState<ServerLoginValidationErrors>({});
  const [showDeviceActivationModal, setShowDeviceActivationModal] = useState<boolean>(false);
  const [activeDeviceNames, setActiveDeviceNames] = useState<string[]>([]);
  const [activeDeviceCount, setActiveDeviceCount] = useState<number>(0);
  const [maxDeviceCount, setMaxDeviceCount] = useState<number>(0);
  const [previousSessionSelected, setPreviousSessionSelected] = useState<string>("");
  const ref1 = useRef<HTMLInputElement>(null);
  const ref2 = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (dataStore.permittedServers) {
      setPreviousSessions(dataStore.permittedServers ? [...dataStore.permittedServers] : []);
    } else {
      setPreviousSessions([]);
    }
  }, [dataStore.permittedServers]);

  const blurAll = () => {
    if (ref1.current) {
      ref1.current.blur();
    }
    if (ref2.current) {
      ref2.current.blur();
    }
  };
  const handleDeviceActivationModalClose = (): void => {
    setShowDeviceActivationModal(false);
  };
  const handleDeviceActivationComplete = useCallback(
    (serverUrl: string): void => {
      setShowDeviceActivationModal(false);
      onStepComplete?.(serverUrl);
    },
    [onStepComplete],
  );

  const handleLogin = async () => {
    //check form validity
    const err: ServerLoginValidationErrors = {};
    let hasErrors: boolean = false;
    if (!localServerUrl) {
      err.serverUrl = {
        message: "Server is required",
        valid: false,
      };
      hasErrors = true;
    }
    setErrors(err);
    if (hasErrors) {
      return;
    }
    blurAll();
    setSubmitDisabled(true);
    dataStore.serverUrl = null;
    dataStore.dbKey = null;
    setCurrentOrder(null);
    try {
      const deviceId = getDeviceId();
      if (!deviceId) {
        toast.error("Device ID not found. Please restart the application.");
        setSubmitDisabled(false);
        return;
      }
      const loginModel: LoginToServerRequest = {
        serverUrl: localServerUrl,
        password: localServerPassword || "",
        deviceId: deviceId,
      };
      const loginServerResponse: LoginToServerResponse | null = await loginToServer(loginModel);
      setSubmitDisabled(false);
      if (!loginServerResponse || !loginServerResponse.success) {
        toast.error(loginServerResponse?.errorMessage || "Wrong Server or Password");
        return;
      } else if (loginServerResponse?.success) {
        if (loginServerResponse.activationStatus && loginServerResponse.activationStatus.success) {
          setActiveDeviceNames(loginServerResponse.activationStatus?.activeDeviceNames || []);
          setActiveDeviceCount(loginServerResponse.activationStatus?.activeDeviceCount || 0);
          setMaxDeviceCount(loginServerResponse.activationStatus?.countLimit || 0);
          // Check if localServerUrl is in permittedServerUrls
          const permittedUrls = loginServerResponse.activationStatus.permittedServerUrls || [];
          dataStore.permittedServers = permittedUrls;
          dataStore.serverUrl = localServerUrl;
          if (!permittedUrls.includes(localServerUrl)) {
            //still need to activate device
            setShowDeviceActivationModal(true);
          }
        }
      }
    } catch (err) {
      const error = err as ApiError;
      setSubmitDisabled(false);
      if (error?.code?.toUpperCase() !== "ERR_NETWORK") {
        toast.error("Login to server failed");
      }
    } finally {
      setSubmitDisabled(false);
      setPreviousSessionSelected("");
    }
  };

  const handleLoginPreviousSession = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const serverUrl = e.target.value;
    setPreviousSessionSelected(serverUrl);
    dataStore.serverUrl = serverUrl;
    if (serverUrl) {
      onStepComplete?.(serverUrl);
    }
  };

  const handleFormKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (document.activeElement === ref1.current) {
        ref2.current?.focus();
      } else {
        handleLogin();
      }
    }
  };
  const handleOnFocusPassword = (e: React.FocusEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.hasAttribute("readonly")) {
      e.target.removeAttribute("readonly");
    }
    e.target.type = "password";
  };

  const handleOnBlurPassword = (e: React.FocusEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.target.setAttribute("readonly", "true");
  };
  const makePasswordInvisible = (e: React.PointerEvent<HTMLElement>): void => {
    const element = document.getElementById("step1_password_toggle");
    if (element) {
      element.className = "fa fa-eye-slash";
    }
    if (ref2.current) {
      ref2.current.type = "password";
    }
    e.stopPropagation();
  };
  const makePasswordVisible = (e: React.PointerEvent<HTMLElement>): void => {
    const element = document.getElementById("step1_password_toggle");
    if (element) {
      element.className = "fa fa-eye";
    }
    if (ref2.current) {
      ref2.current.type = "text";
    }
    e.stopPropagation();
  };
  return (
    <>
      <div className="row mb-2">
        <div className="col-lg-4 offset-lg-4 col-md-6 offset-md-3 col-sm-8 offset-sm-2 col-12 mb-2 mt-4">
          {!previousSessionSelected && (
            <div className="d-grid gap-2" onKeyDown={handleFormKeyDown}>
              <div>
                <label htmlFor="serverUrl">Server Address</label>
                <div className="d-flex">
                  <input
                    ref={ref1}
                    name={`a-${Math.floor(Math.random() * 1000)}-${Math.floor(Math.random() * 1000)}`}
                    id={`a-${Math.floor(Math.random() * 1000)}-${Math.floor(Math.random() * 1000)}`}
                    type="text"
                    className={errors?.serverUrl ? "form-control is-invalid" : "form-control"}
                    autoComplete="new-password"
                    value={localServerUrl}
                    onChange={(e) => {
                      setLocalServerUrl(e.target.value);
                    }}
                  />
                </div>
                {errors?.serverUrl && (
                  <div className="invalid-feedback d-block">
                    <div>{errors.serverUrl?.message}</div>
                  </div>
                )}
              </div>
              <div>
                <label htmlFor="serverPassword">Server Password</label>
                <div className="d-flex">
                  <div className="input-group">
                    <input
                      ref={ref2}
                      name={`a-${Math.floor(Math.random() * 1000)}-${Math.floor(Math.random() * 1000)}`}
                      id={`a-${Math.floor(Math.random() * 1000)}-${Math.floor(Math.random() * 1000)}`}
                      readOnly={true}
                      type="text"
                      className={errors?.serverPassword ? "form-control is-invalid" : "form-control"}
                      autoComplete="chrome-off"
                      onFocus={handleOnFocusPassword}
                      onBlur={handleOnBlurPassword}
                      value={localServerPassword}
                      onChange={(e) => {
                        setLocalServerPassword(e.target.value);
                      }}
                    />
                    <span className="input-group-text" onPointerDown={makePasswordVisible} onPointerUp={makePasswordInvisible}>
                      <i id="step1_password_toggle" className="fa fa-eye-slash" style={{ cursor: "pointer" }}></i>
                    </span>
                  </div>
                  <div>
                    <button disabled={submitDisabled} className="btn btn-primary ms-2" style={{ width: "50px" }} type="button" onClick={handleLogin}>
                      <i className="fa fa-solid fa-arrow-right" aria-hidden="true"></i>
                    </button>
                  </div>
                </div>
                {errors?.serverPassword && (
                  <div className="invalid-feedback d-block">
                    <div>{errors.serverPassword?.message}</div>
                  </div>
                )}
              </div>
              <div className="d-grid">{submitDisabled && <p className="text-center">Please wait...</p>}</div>
            </div>
          )}
          {previousSessions && previousSessions.length > 0 && (
            <div>
              {!previousSessionSelected && (
                <div className="text-center">
                  <b>or</b>
                </div>
              )}
              <div className="mt-2">
                <select className="form-select mb-4" aria-label="Previous sessions" value={previousSessionSelected} onChange={handleLoginPreviousSession}>
                  <option value="" key="">
                    Select from previous sessions
                  </option>
                  {previousSessions.map((ses) => (
                    <option value={ses} key={ses}>
                      {ses}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      </div>
      <ModalRegisterDevice
        modalOpen={showDeviceActivationModal}
        onClose={handleDeviceActivationModalClose}
        onRegistrationComplete={handleDeviceActivationComplete}
        passActiveDeviceNames={activeDeviceNames}
        passActiveDeviceCount={activeDeviceCount}
        passMaxDeviceCount={maxDeviceCount}
      />
    </>
  );
});

export default Step1;
